import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import PropTypes from 'prop-types';

/**
 * A reusable confirmation modal with title, message, and confirm/cancel actions.
 *
 * @param {Object} props
 * @param {boolean} props.show - Whether the modal is visible
 * @param {Function} props.onHide - Called when modal is dismissed
 * @param {Function} props.onConfirm - Called when confirm button is clicked
 * @param {string} props.title - Modal title
 * @param {string} props.message - Main message text
 * @param {string} props.detail - Secondary detail text (muted)
 * @param {string} props.confirmLabel - Label for confirm button (default: "Confirm")
 * @param {string} props.confirmingLabel - Label while processing (default: "Processing...")
 * @param {string} props.variant - Button variant (default: "warning")
 * @param {boolean} props.isProcessing - Whether action is in progress
 */
function ConfirmationModal({
  show,
  onHide,
  onConfirm,
  title,
  message,
  detail,
  confirmLabel = 'Confirm',
  confirmingLabel = 'Processing...',
  variant = 'warning',
  isProcessing = false,
}) {
  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>{message}</p>
        {detail && <p className="text-muted small">{detail}</p>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isProcessing}>
          Cancel
        </Button>
        <Button variant={variant} onClick={onConfirm} disabled={isProcessing}>
          {isProcessing ? confirmingLabel : confirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

ConfirmationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  detail: PropTypes.string,
  confirmLabel: PropTypes.string,
  confirmingLabel: PropTypes.string,
  variant: PropTypes.string,
  isProcessing: PropTypes.bool,
};

export default ConfirmationModal;
